// src/pages/SupplyChainManage.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Search as SearchIcon,
  CheckCircle2,
  AlertTriangle,
  X,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import {
  FLAG,
  COUNTRY_OPTIONS,
  SIZE_OPTIONS,
  loadCompanies,
  saveCompanies,
} from "../data/supplyChainCompanies";

const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";

const primaryBtn =
  "inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

const ghostBtn =
  "inline-flex items-center gap-1.5 rounded-md border border-sky-900/30 hover:bg-sky-900/30 px-2.5 py-1.5 text-[12px] text-neutral-300 hover:text-sky-300 transition-colors";

const inputClass =
  "w-full rounded-md border border-sky-900/30 bg-[#0b1426] px-3 py-2 text-[13px] text-white outline-none focus:border-sky-500/50 transition-colors disabled:opacity-50";

const labelClass =
  "block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium";

function todayStamp() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
}

function normalizeDomain(input) {
  return input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/.*$/, "");
}

function inferName(domain) {
  const root = domain.split(".")[0] || "";
  return root
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function SupplyChainManage() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  const [rows, setRows] = useState(() => loadCompanies());
  const [query, setQuery] = useState("");

  // Add form
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [size, setSize] = useState("201 – 500");
  const [country, setCountry] = useState("US");

  // Feedback
  const [notice, setNotice] = useState(null); // { kind: 'success' | 'error', msg }
  // Confirm-delete state — domain currently awaiting confirmation
  const [confirmRemove, setConfirmRemove] = useState(null);

  useEffect(() => {
    saveCompanies(rows);
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.domain.toLowerCase().includes(q) ||
        r.country.toLowerCase().includes(q)
    );
  }, [rows, query]);

  const handleAdd = (e) => {
    e.preventDefault();
    const clean = normalizeDomain(domain);
    if (!clean) {
      setNotice({ kind: "error", msg: "Domain is required." });
      return;
    }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(clean)) {
      setNotice({ kind: "error", msg: "Enter a valid domain (e.g. acme.com)." });
      return;
    }
    if (rows.some((r) => r.domain === clean)) {
      setNotice({ kind: "error", msg: `${clean} is already being monitored.` });
      return;
    }
    const finalName = (name.trim() || inferName(clean));
    const newRow = {
      domain: clean,
      name: finalName,
      sighting: "—",
      identity: 0,
      malware: { v: 0, d: todayStamp() },
      breach: { v: 0, d: todayStamp() },
      phish: { v: 0, d: todayStamp() },
      combo: { v: 0, d: todayStamp() },
      size,
      country,
      trend: null,
      added: todayStamp(),
    };
    setRows((prev) => [newRow, ...prev]);
    setDomain("");
    setName("");
    setNotice({ kind: "success", msg: `${finalName} added to monitoring.` });
  };

  const handleRemove = (target) => {
    setRows((prev) => prev.filter((r) => r.domain !== target));
    setConfirmRemove(null);
    setNotice({ kind: "success", msg: `${target} removed from monitoring.` });
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/supply-chain")}
              className={ghostBtn}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">
              Manage Companies
            </h1>
            <span className="ml-2 text-[12px] text-neutral-500">
              {rows.length} monitored
            </span>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="supplychain"
          onChange={() => {}}
        />

        <main className="relative flex-1 px-4 md:px-6 pb-10 space-y-4">
          {/* Feedback banner */}
          {notice && (
            <div
              role="alert"
              className={`relative rounded-md px-3 py-2 text-[13px] border ${
                notice.kind === "success"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-red-500/30 bg-red-500/10 text-red-200"
              }`}
            >
              <div className="flex items-start gap-2 pr-6">
                {notice.kind === "success" ? (
                  <CheckCircle2 className="h-4 w-4 mt-[2px]" />
                ) : (
                  <AlertTriangle className="h-4 w-4 mt-[2px]" />
                )}
                <span>{notice.msg}</span>
              </div>
              <button
                type="button"
                onClick={() => setNotice(null)}
                className="absolute right-1.5 top-1.5 rounded-md p-1 hover:bg-white/10"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Add form */}
          <section className={`${panel}`}>
            <div className="border-b border-sky-900/30 px-4 py-3">
              <div className="text-[14px] font-semibold text-neutral-100">
                Add a company
              </div>
              <div className="text-[12px] text-neutral-500 mt-0.5">
                Enter the vendor's primary domain. We'll start monitoring exposed credentials, infostealer hits and breach mentions within minutes.
              </div>
            </div>
            <form onSubmit={handleAdd} className="px-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-4">
                  <label htmlFor="domain" className={labelClass}>Domain *</label>
                  <input
                    id="domain"
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="acme.com"
                    className={inputClass}
                    required
                  />
                </div>
                <div className="md:col-span-4">
                  <label htmlFor="name" className={labelClass}>Display name</label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Auto-derived from domain"
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="size" className={labelClass}>Size</label>
                  <select
                    id="size"
                    value={size}
                    onChange={(e) => setSize(e.target.value)}
                    className={inputClass}
                  >
                    {SIZE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="country" className={labelClass}>Country</label>
                  <select
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className={inputClass}
                  >
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-end">
                <button type="submit" className={primaryBtn}>
                  <Plus className="h-3.5 w-3.5" />
                  Add Company
                </button>
              </div>
            </form>
          </section>

          {/* Monitored companies list */}
          <section className={`${panel}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-sky-900/30 px-4 py-3">
              <div className="text-[14px] font-semibold text-neutral-100">
                Monitored companies{" "}
                <span className="text-neutral-500 font-normal">
                  ({filtered.length})
                </span>
              </div>
              <div className="relative">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, domain or country"
                  className="rounded-md border border-sky-900/30 bg-[#0b1426] pl-8 pr-3 py-1.5 text-[12px] text-neutral-200 placeholder:text-neutral-600 outline-none focus:border-sky-500/50 transition-colors w-72"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead className="bg-[#0b1426]/60">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Company</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Domain</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Size</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Country</th>
                    <th className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-400">Added</th>
                    <th className="px-4 py-2.5 text-right text-[11px] uppercase tracking-wider font-medium text-neutral-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const isConfirming = confirmRemove === r.domain;
                    return (
                      <tr
                        key={r.domain}
                        className={`border-b border-sky-900/20 hover:bg-sky-900/15 transition-colors ${i % 2 === 0 ? "bg-transparent" : "bg-[#091020]/40"}`}
                      >
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-100 font-medium">
                          {r.name}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-400">
                          {r.domain}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-300 tabular-nums">
                          {r.size}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20">
                          <span className="text-[16px] leading-none mr-1.5" title={r.country}>
                            {FLAG[r.country] || ""}
                          </span>
                          <span className="text-[12px] text-neutral-400">{r.country}</span>
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-[13px] text-neutral-400 tabular-nums">
                          {r.added || "—"}
                        </td>
                        <td className="px-4 py-3 border-t border-sky-900/20 text-right">
                          {isConfirming ? (
                            <div className="inline-flex items-center gap-1.5">
                              <span className="text-[12px] text-rose-300">Remove?</span>
                              <button
                                type="button"
                                onClick={() => handleRemove(r.domain)}
                                className="inline-flex items-center gap-1 rounded-md bg-rose-600/80 hover:bg-rose-600 px-2 py-1 text-[12px] font-semibold text-white"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmRemove(null)}
                                className="inline-flex items-center gap-1 rounded-md border border-sky-900/40 hover:bg-sky-900/30 px-2 py-1 text-[12px] text-neutral-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmRemove(r.domain)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 hover:bg-rose-500/15 px-2 py-1 text-[12px] text-rose-300 transition-colors"
                              aria-label={`Remove ${r.name}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-[13px] text-neutral-500">
                        {query
                          ? "No companies match your search."
                          : "No companies are being monitored yet. Add one above to get started."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
